import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiUrl } from '../../../lib/api'
import {
  fetchSellerHomeCatalogue,
  fetchSellerHomeHealth,
  fetchSellerHomeListings,
  fetchSellerHomeSummary,
  sellerHomeKeys,
  SellerHomeHttpError,
} from './seller-home-api'
import { SellerHomeView, type SellerHomeModule } from './SellerHomeView'

function toModule<T>(query: {
  data?: T
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => unknown
}): SellerHomeModule<T> {
  if (query.data !== undefined) return { status: 'ready', data: query.data, retry: () => void query.refetch() }
  if (query.isLoading) return { status: 'loading' }
  if (query.isError && query.error instanceof SellerHomeHttpError && query.error.status === 403) {
    return { status: 'restricted', retry: () => void query.refetch() }
  }
  return { status: 'error', retry: () => void query.refetch() }
}

export function SellerHome() {
  const queryClient = useQueryClient()
  const [favoriteOverrides, setFavoriteOverrides] = useState<Record<string, boolean>>({})
  const summary = useQuery({ queryKey: sellerHomeKeys.summary, queryFn: () => fetchSellerHomeSummary() })
  const health = useQuery({ queryKey: sellerHomeKeys.health, queryFn: fetchSellerHomeHealth })
  const listings = useQuery({ queryKey: sellerHomeKeys.listings, queryFn: fetchSellerHomeListings })
  const catalogue = useQuery({ queryKey: sellerHomeKeys.catalogue, queryFn: fetchSellerHomeCatalogue })

  const favorite = useMutation({
    mutationFn: async ({ id, next }: { id: string; next: boolean }) => {
      const response = await fetch(apiUrl(`/master-catalog/${id}/favorite`), {
        method: next ? 'POST' : 'DELETE',
      })
      if (!response.ok) throw new SellerHomeHttpError(response.status)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seller', 'catalog'] })
    },
  })

  const catalogueModule = toModule(catalogue)
  if (catalogueModule.status === 'ready' && catalogueModule.data) {
    catalogueModule.data = catalogueModule.data.map((product) => ({
      ...product,
      isFavorite: favoriteOverrides[product.id] ?? product.isFavorite,
    }))
  }

  const toggleFavorite = (id: string) => {
    const product = catalogueModule.data?.find((item) => item.id === id)
    if (!product) return
    const next = !product.isFavorite
    setFavoriteOverrides((previous) => ({ ...previous, [id]: next }))
    favorite.mutate(
      { id, next },
      {
        onError: () => setFavoriteOverrides((previous) => ({
          ...previous,
          [id]: product.isFavorite,
        })),
      },
    )
  }

  return (
    <SellerHomeView
      summary={toModule(summary)}
      health={toModule(health)}
      listings={toModule(listings)}
      catalogue={catalogueModule}
      onToggleFavorite={toggleFavorite}
    />
  )
}
